const AmazonDataProtectionPolicy = () => {
  return (
    <div className="overflow-hidden">
      <section className="relative py-24 hero-gradient mesh-gradient">
        <div className="absolute top-20 right-10 w-72 h-72 bg-purple-400/20 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold font-['Outfit'] tracking-tighter mb-6">
              Amazon Data Protection Policy
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Controls for Amazon SP-API data, seller account information, and Amazon customer PII handled by Jnan Innovative IT Solutions.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-slate max-w-none">
            <p><strong>Effective Date:</strong> 6/28/2026</p>

            <h2>1. Purpose</h2>
            <p>
              This policy describes how Jnan Innovative IT Solutions protects Amazon Selling Partner API (SP-API) data, Amazon seller account data, and Amazon customer personally identifiable information (PII). It supports our privacy, data handling, data classification, retention, incident response, and information security policies.
            </p>
            <br />

            <h2>2. Approved Use of Amazon Data</h2>
            <p>
              We access Amazon SP-API data only for authorized seller operations and only to the extent required to provide agreed services. Amazon customer PII is requested and processed only when necessary for approved purposes such as merchant-fulfilled shipping, carrier label generation, delivery coordination, tax invoicing, tax remittance, or legal/regulatory obligations.
            </p>
            <p>
              We do not use Amazon customer PII for marketing, retargeting, customer profiling, review solicitation outside Amazon-approved flows, resale, enrichment, unrelated analytics, or cross-seller aggregation.
            </p>
            <br />

            <h2>3. Data Minimization</h2>
            <p>
              We request only the minimum Amazon data fields required for the approved business purpose. Access to restricted SP-API roles and restricted data is limited to documented use cases and approved personnel or services.
            </p>
            <br />

            <h2>4. Data Handling, Classification, and Processing Records</h2>
            <ul>
              <li>Amazon customer PII is classified as Restricted/PII.</li>
              <li>Amazon seller business data is classified as Confidential unless otherwise approved.</li>
              <li>Documented data handling and classification policies define collection, processing, storage, transmission, access, retention, and disposal requirements.</li>
              <li>Processing records identify the Amazon data fields processed, source API/report, processing purpose, storage location, access roles, retention period, sharing or subprocessor involvement, and deletion method.</li>
              <li>Employees and contractors with access to Amazon data are subject to confidentiality and security obligations.</li>
            </ul>
            <br />

            <h2>5. Access Control</h2>
            <ul>
              <li>Access to Amazon data is restricted by role-based access control and least-privilege permissions.</li>
              <li>Production access is limited to authorized personnel with a business need.</li>
              <li>Administrative access is reviewed periodically and removed when no longer required.</li>
              <li>Amazon credentials, tokens, and secrets are never hardcoded in source code or stored in plaintext.</li>
            </ul>
            <br />

            <h2>6. Encryption and Key Management</h2>
            <p>
              Amazon customer PII stored by our application is encrypted at rest using AES-256 or stronger, exceeding the AES-128 minimum. Encryption applies to databases, object storage, block volumes, file systems, backups, and approved systems that store or process Amazon PII.
            </p>
            <p>
              SP-API credentials, Login with Amazon credentials, refresh tokens, database credentials, and encryption secrets are stored in approved secret management or key management systems. Key management controls cover key generation, secure storage, access control, rotation, revocation, and emergency replacement after suspected compromise.
            </p>
            <br />

            <h2>7. Vulnerability Management</h2>
            <ul>
              <li>Systems that store, process, or transmit Amazon data are included in vulnerability management scope.</li>
              <li>Vulnerability scans are performed at least every 30 days across in-scope application, infrastructure, database, and cloud assets.</li>
              <li>Application code, dependencies, containers, and infrastructure configuration are scanned before production releases where applicable.</li>
              <li>Penetration testing is conducted at least every 365 days by qualified internal or third-party testers.</li>
              <li>Critical findings are remediated within 7 days of discovery and high-risk findings within 30 days, unless a documented risk exception is approved.</li>
            </ul>
            <br />

            <h2>8. Audit Logging and Monitoring</h2>
            <ul>
              <li>Centralized audit logs are maintained for systems that access, process, or store Amazon data.</li>
              <li>Logs include security-relevant events such as authentication, authorization, API access, administrative activity, data access, data changes, configuration changes, errors, and suspicious activity.</li>
              <li>Logs include timestamp, user or service identity, source IP where available, event type, success/failure status, affected resource, and action performed.</li>
              <li>Logs are protected against unauthorized access and tampering.</li>
              <li>Amazon customer PII is redacted from logs unless legally required.</li>
              <li>Logs are retained for at least 12 months.</li>
              <li>Alerts are reviewed in real time where configured, and documented manual log reviews are performed at least bi-weekly.</li>
            </ul>
            <br />

            <h2>9. Retention and Secure Deletion</h2>
            <p>
              Amazon customer PII is retained only as long as necessary for the approved purpose and is deleted no later than 30 days after order delivery unless longer retention is legally required. When Amazon data is no longer required, it is securely deleted from active systems and removed from backups according to backup lifecycle controls.
            </p>
            <br />

            <h2>10. Incident Response</h2>
            <p>
              Suspected unauthorized access, disclosure, alteration, loss, or misuse of Amazon data is handled under our incident response process. Response activities include detection, containment, investigation, remediation, recovery, documentation, and post-incident review. Credentials and keys are rotated when compromise is suspected.
            </p>
            <br />

            <h2>11. Third Parties and Subprocessors</h2>
            <p>
              Third parties that process Amazon data on our behalf are limited to approved business purposes, reviewed for security risk, and required to protect data through contractual confidentiality and security obligations.
            </p>
            <br />

            <h2>12. Contact</h2>
            <p>
              Jnan Innovative IT Solutions<br />
              Email: info@jnanit.com<br />
              Website: https://jnanit.com
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AmazonDataProtectionPolicy;
